import axios, { AxiosInstance } from "axios"
import { DateTime } from "luxon"

import { ApiError } from "../../../core/api/errorTypes"

export enum MenuDisplayConnectivityQuery {
    STATUS = "public-menu-display-connectivity-status",
}

export enum ConnectivityStatus {
    ONLINE = "ONLINE",
    SERVER_DOWN = "SERVER_DOWN",
    LOCAL_OFFLINE = "LOCAL_OFFLINE",
}

const PROBE_TIMEOUT_MS = 3000
// Cloudflare's trace endpoint (CORS-open, no custom headers so the request needs no preflight):
// reaching it proves the local internet connection works even when our backend does not respond.
const PUBLIC_PROBE_URL = "https://1.1.1.1/cdn-cgi/trace"

/**
 * Tells a dead local connection apart from our backend being unreachable, so the display never
 * claims "no internet" just because the server is down (ported from gopay-info-display PR #89).
 */
export async function diagnoseConnectivity(
    _params: Record<string, never>,
    axiosInstance: AxiosInstance,
    signal?: AbortSignal,
): Promise<ConnectivityStatus> {
    const cacheBuster = { _t: DateTime.utc().toMillis() }

    try {
        await axiosInstance.get("/public/uptime/system", { params: cacheBuster, timeout: PROBE_TIMEOUT_MS, signal })
        return ConnectivityStatus.ONLINE
    } catch (serverError) {
        // Any response at all (even an error status) means the connection to the backend works.
        if (serverError instanceof ApiError && serverError.originalErrorResponse) return ConnectivityStatus.ONLINE

        try {
            await axios.get(PUBLIC_PROBE_URL, { params: cacheBuster, timeout: PROBE_TIMEOUT_MS, signal })
            return ConnectivityStatus.SERVER_DOWN
        } catch (publicError) {
            if (axios.isAxiosError(publicError) && publicError.response) return ConnectivityStatus.SERVER_DOWN
            return ConnectivityStatus.LOCAL_OFFLINE
        }
    }
}
